/* eslint-disable lines-between-class-members */

import { OTSignal } from './signal';
import { OTSession } from './session';
import { OTPublisher } from './publisher';
import { ReduxEmitter } from '../helpers/reduxEmitter';
import { otEventManager } from '../helpers/eventManager';
import {
  sentryCaptureError,
  trackAppAction,
  trackError,
  UserActionSeverity,
} from '../../../utils/sentry/sentryMethods';

export class OTCore extends ReduxEmitter {
  scheduledReconnectPublisher = false;
  scheduledReconnectSession = false;
  isOnline = true;
  displayError = () => {};

  constructor(options) {
    super(options.dispatch);
    this.credentials = options.config;
    this.isOnline = options.isOnline;
    this.user = options.user;
    this.displayError = options.handleGlobalError;

    this.otSession = new OTSession({
      config: this.credentials,
      dispatch: options.dispatch,
    });
    this.otPublisher = new OTPublisher({
      user: this.user,
      dispatch: options.dispatch,
      otSession: this.otSession,
    });
    this.otSignal = new OTSignal({
      dispatch: options.dispatch,
      otSession: this.otSession,
    });
  }

  handleException = event => {
    if (event.code === 1500) {
      event.preventDefault();
      trackAppAction(
        '[ot-core] exception code:1500; -> reconnect publisher',
        UserActionSeverity.Error,
      );
      console.warn('1500 RECONNECT HERE', event);
      this.reconnectPublisher();
      return;
    }

    if (event.code === 1004) {
      console.warn('1004 SESSION EXPIRED HERE', event);
      trackAppAction(
        '[ot-core] exception code:1004; -> destroy',
        UserActionSeverity.Error,
      );
      return;
    }

    trackError(event);
    sentryCaptureError('[ERROR] OT EXCEPTION');
    console.error(' UNHADLED EXCEPTION', event);
  };

  setupListeners() {
    otEventManager.onReconnect(() => this.reconnectPublisher());
    otEventManager.onRestart(() => this.reconnectToSession());
    OT.on('exception', this.handleException);
  }

  clearListeners() {
    otEventManager.offReconnect();
    otEventManager.offRestart();
    OT.off('exception', this.handleException);
  }

  get currentMediaDevices() {
    return this.otPublisher.initialMediaDevices;
  }

  updateOnlineStatus(isOnline) {
    console.warn('internet connection ', isOnline ? 'online' : 'offline');
    this.isOnline = isOnline;
    if (isOnline) {
      if (this.scheduledReconnectPublisher) {
        return this.reconnectPublisher();
      }

      if (this.scheduledReconnectSession) {
        return this.reconnectToSession();
      }
    }
    return null;
  }

  initiateModels() {
    this.setupListeners();
    this.otSession.init();
    this.otSignal.init();
  }

  async startVideoCall() {
    trackAppAction('[ot-core] start virtual call');
    try {
      console.info('============== S T A R T    C A L L =============');
      this.dispatchToggleLoading(true);
      this.initiateModels();
      await this.otPublisher.setupPublisher();
      await this.otSession.connect();
      await this.otPublisher.publishIntoSession();
      this.otSession.setTargetStreamIdOnSessionInit();
      trackAppAction('[ot-core] connected');
      console.info('============== C A L L   S T A R T E D  =============');
    } catch (error) {
      console.error('[ot-core] error during start virtual call', error);
      trackAppAction(
        '[ot-core] error during start virtual call',
        UserActionSeverity.Error,
      );
      trackError(error);
      sentryCaptureError('[ERROR] START VISIT');
      this.displayError(error && error.name);
    } finally {
      this.dispatchToggleLoading(false);
    }
  }

  async reconnectPublisher() {
    if (!this.isOnline) {
      this.scheduledReconnectPublisher = true;
      console.warn(
        'app is offline ---> schedule reconnect publisher after connection to network',
      );
      return;
    }
    trackAppAction('[ot-core] reconnect publisher');

    try {
      console.info('== R E C O N N E C T I N G    P U B L I S H E R ===');
      this.dispatchToggleLoading(true);
      this.otPublisher.destroy();
      await this.otPublisher.setupPublisher();
      await this.otPublisher.publishIntoSession();
      this.otSession.setTargetStreamIdOnSessionInit();
      trackAppAction('[ot-core] connected');
      console.info('= F I N I S H    R E C O N N E C T   P U B L I S H E R =');
    } catch (error) {
      console.error('error during reconnect PUBLISHER', error);
      trackAppAction(
        '[ot-core] error during reconnect publisher',
        UserActionSeverity.Error,
      );
      trackError(error);
      sentryCaptureError('[ERROR] RECONNECT PUBLISHER');
    } finally {
      this.dispatchToggleLoading(false);
      this.scheduledReconnectPublisher = false;
    }
  }

  async reconnectToSession() {
    if (!this.isOnline) {
      this.scheduledReconnectSession = true;
      console.warn(
        'app is offline ---> schedule reconnect SESSION after connection to network',
      );
      return;
    }

    console.info('== R E C O N N E C T I N G    S E S S I O N ===');
    trackAppAction('[ot-core] reconnect to session');
    try {
      this.destroy();
      await this.startVideoCall();
    } catch (error) {
      console.error('error during reconnect SESSION', error);
      trackAppAction(
        '[ot-core] error during reconnect to session',
        UserActionSeverity.Error,
      );
      trackError(error);
      sentryCaptureError('[ERROR] RECONNECT SESSION');
    } finally {
      this.scheduledReconnectSession = false;
    }
  }

  destroy() {
    console.info('-------- D E S T R O Y I N G --------');
    this.dispatchReset();
    this.clearListeners();
    this.otPublisher.destroy();
    this.otSignal.destroy();
    this.otSession.destroy();
    console.info('-------- D E S T R O Y E D --------');
  }
}



// WEBPACK FOOTER //
// ./src/redux/opentok/core/index.js